/**
 * Shared layout/visual building blocks for every report tab in the
 * report-dashboard shell (Geographical Overview, Vote Tracking, and whichever
 * reports come next), styled to match the HD-10 reference reports'
 * editorial layout (centered narrow column, flat bordered cards, h2/h3
 * section rhythm, plain bar charts) while sourcing every color from this
 * app's actual MUI theme rather than hardcoding hex values. Reused across
 * report tabs so the experience stays consistent as more are added.
 *
 * Party colors are a semantic data legend (R = warm/red, D = cool/teal),
 * not a branding choice, but they're still derived from theme palette
 * ramps (error/info/grey) so a theme change propagates here too.
 */
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

export const getPartyColor = (theme, calcparty) =>
  ({
    'Solid Republican': theme.palette.error.dark,
    'Soft Republican': theme.palette.error.light,
    'Swing / Unaffiliated': theme.palette.grey[500],
    'Soft Democrat': theme.palette.info.light,
    'Solid Democrat': theme.palette.info.dark,
  }[calcparty] ?? theme.palette.grey[400]);

/**
 * Color map for the fixed Republican/Democrat/Other reg_party taxonomy (see
 * regPartyGroupLabel in api/geographical-overview.js) - mapped explicitly by
 * label rather than positionally, so a state's bucket colors stay stable
 * even if Democrat/Other end up empty and get filtered out of a legend.
 * Shared by PartisanCompositionCard's Registered mode, GroupTurnoutTable's
 * Registered Party tab, and BlocPacingCard's Registered mode.
 */
export const getRegPartyGroupColor = (theme, label) =>
  ({
    Republican: theme.palette.error.dark,
    Democrat: theme.palette.info.dark,
    Other: theme.palette.grey[500],
  }[label] ?? theme.palette.grey[400]);

/** 5-step diverging scale for R-lean deviation, from the theme's red/grey/info ramps. */
export const getLeanColor = (theme, dev) => {
  if (dev >= 8) return theme.palette.error.dark;
  if (dev >= 3) return theme.palette.error.main;
  if (dev >= -2) return theme.palette.grey[400];
  if (dev >= -7) return theme.palette.info.main;
  return theme.palette.info.dark;
};

/** Color for an R-lean % value shown as a pill (precinct table). */
export const getRLeanPillColor = (theme, rlean) => {
  if (rlean >= 55) return theme.palette.error.dark;
  if (rlean >= 48) return theme.palette.error.light;
  if (rlean >= 42) return theme.palette.grey[500];
  return theme.palette.info.dark;
};

/** h2-equivalent: section title + muted subtitle, sitting directly on the page. */
export const SectionHeading = ({ title, subtitle }) => (
  <Stack gap={0.25} sx={{ mt: 1 }}>
    <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Stack>
);

/**
 * h3-equivalent: a card-local title + caption, used by cards that carry
 * their own heading - matching HD-10's card-title treatment (15-16px/800,
 * e.g. "Field targets", "Sentiment", "Key dates", "Top issues").
 */
export const CardHeading = ({ title, caption }) => (
  <Stack gap={0.25}>
    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
      {title}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.disabled">
        {caption}
      </Typography>
    )}
  </Stack>
);

/**
 * Flat bordered KPI tile, matching HD-10's `.kpi` literally: 12px/600 label
 * in sentence case (not uppercase - HD-10's KPI labels read as phrases like
 * "Days to election", not eyebrow text), a bold 28px/800 value, and a
 * 11.5px/600 caption underneath.
 */
/**
 * `caption2` is a second caption line, matching HD-10's KPI tiles (each has
 * both a `cnt` and `cnt2` line - e.g. Win number shows "X likely voters"
 * then "Y% projected turnout" underneath) - omit it for tiles that only
 * need one line.
 */
/**
 * `loading` renders a skeleton where the value goes, instead of the value.
 *
 * Without it a tile awaiting its first response is indistinguishable from one reporting nothing:
 * both read `--`, and on a strip that fetches separately from the table beneath it (so the rows
 * are already on screen) the natural reading is "there is no data", not "still counting".
 *
 * The label and captions stay put, so the tile does not change size when the number lands.
 */
export const KpiTile = ({ label, value, caption, caption2, valueColor, gridProps, loading }) => (
  <Grid item xs={12} sm={6} md={3} {...gridProps}>
    <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Stack gap={0.5}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {loading ? (
          <Skeleton variant="text" width="60%" sx={{ fontSize: 28, transform: 'none', my: 0.5 }} />
        ) : (
          <Typography
            variant="h3"
            sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', color: valueColor }}
          >
            {value}
          </Typography>
        )}
        {caption && (
          <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
            {caption}
          </Typography>
        )}
        {caption2 && (
          <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
            {caption2}
          </Typography>
        )}
      </Stack>
    </Card>
  </Grid>
);

/**
 * Single labeled horizontal bar row: label + track + filled % + value.
 * Matches HD-10's `.bar-row`. The label always renders in full - it wraps
 * onto a second line rather than truncating, since labels here include full
 * modeled-party names (e.g. "Swing / Unaffiliated") that must never be cut.
 * `labelWidth` is a fixed width (not a minimum) so every row in a group -
 * regardless of label length - has its bar start at the same x position;
 * pass the same `labelWidth` to every BarRow in a set you want aligned.
 * `valueWidth` widens the value column for callers whose `valueText` carries
 * more than a percentage (e.g. survey results show "72% (412)").
 */
export const BarRow = ({ label, pct, color, valueText, labelWidth = 110, valueWidth = 42 }) => (
  <Stack direction="row" alignItems="center" gap={1.25}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ width: labelWidth, flexShrink: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: 9, bgcolor: 'action.hover', borderRadius: 5, overflow: 'hidden' }}>
      <Box
        sx={{
          width: `${Math.max(0, Math.min(100, pct))}%`,
          height: '100%',
          bgcolor: color || 'primary.main',
        }}
      />
    </Box>
    <Typography variant="body2" sx={{ width: valueWidth, textAlign: 'right', fontWeight: 600 }}>
      {valueText ?? `${pct}%`}
    </Typography>
  </Stack>
);

/**
 * `BarRow`'s track split into parts of one whole rather than filled to a
 * single percentage - for a total that is definitionally the sum of its
 * segments, where a column per segment would be three numbers saying what one
 * bar says. Each segment carries its own tooltip with the exact figure, so
 * precision is a hover away rather than a column.
 *
 * Segments are `[{ key, label, value, color }]`; widths are shares of their
 * own sum, so a caller never has to pass percentages. A zero-width segment
 * renders nothing rather than a hairline.
 *
 * `fill` is how much of the track the whole stack occupies, for rows that
 * are parts of something bigger than themselves - a leaderboard where the
 * bar's length is a contributor's share of the total and its split is the
 * quality of that contribution. Left at 100 the stack fills the track and
 * only the split carries meaning.
 */
export const StackedBarRow = ({ segments, valueText, valueWidth = 42, height = 9, fill = 100 }) => {
  const total = (segments || []).reduce((sum, segment) => sum + (segment.value || 0), 0);

  return (
    <Stack direction="row" alignItems="center" gap={1.25}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          height,
          bgcolor: 'action.hover',
          borderRadius: 5,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            // A contributor with a real but tiny share still gets a visible mark
            // rather than nothing at all.
            width: `${total > 0 ? Math.max(1, Math.min(100, fill)) : 0}%`,
            height: '100%',
          }}
        >
          {total > 0 &&
            segments.map((segment) => {
              const pct = ((segment.value || 0) * 100) / total;
              if (!pct) return null;
              return (
                <Tooltip
                  key={segment.key}
                  title={`${segment.label}: ${(segment.value || 0).toLocaleString()} (${Math.round(
                    pct
                  )}%)`}
                >
                  <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: segment.color }} />
                </Tooltip>
              );
            })}
        </Box>
      </Box>
      {valueText !== undefined && (
        <Typography variant="body2" sx={{ width: valueWidth, textAlign: 'right', fontWeight: 600 }}>
          {valueText}
        </Typography>
      )}
    </Stack>
  );
};

/**
 * Single ranked-list row: name on the left, a primary stat + secondary
 * (muted) stat on the right. Shared by Geographical Overview's priority
 * precinct tables and Daily Brief's priority precincts card - same
 * rank-list shape, different stat columns per use.
 */
export const PriorityRow = ({ name, primary, primaryColor, secondary }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{
      py: 0.875,
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Typography variant="body2">{name}</Typography>
    <Stack direction="row" gap={1.75} alignItems="baseline">
      <Typography variant="body2" sx={{ fontWeight: 600, color: primaryColor }}>
        {primary}
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ minWidth: 62, textAlign: 'right' }}>
        {secondary}
      </Typography>
    </Stack>
  </Stack>
);

/**
 * Segmented pill toggle, matching HD-10's repeated grey-track/white-active-
 * segment control (Field targets' Today/Yesterday/This Week, Calendar &
 * Pacing's Direct/Digital filter, Vote Tracking's party-mode toggle, etc.) -
 * built once here since several report tabs reuse this exact pattern.
 * `options` is `[{ value, label }]`; fully controlled via `value`/`onChange`.
 */
export const SegmentedToggle = ({ options, value, onChange }) => (
  <Stack direction="row" sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 0.375 }}>
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <Box
          key={opt.value}
          component="button"
          type="button"
          onClick={() => onChange(opt.value)}
          sx={{
            font: 'inherit',
            fontSize: 11.5,
            fontWeight: active ? 700 : 600,
            px: 1.5,
            py: 0.625,
            border: 'none',
            borderRadius: 1,
            cursor: 'pointer',
            color: active ? 'text.primary' : 'text.secondary',
            bgcolor: active ? 'background.paper' : 'transparent',
            boxShadow: active ? 1 : 'none',
          }}
        >
          {opt.label}
        </Box>
      );
    })}
  </Stack>
);

/**
 * Standalone chip-style tabs, matching HD-10's dimension-tab row (Inferred
 * Party / Age / Income / Education / Religion / Ethnicity / Gender on
 * Geographical Overview's group-turnout table) - each option is its own
 * rounded chip with a solid colored fill when active, distinct from
 * `SegmentedToggle`'s single grouped track with a sliding white-active
 * segment (used for true binary toggles like Registered/Inferred).
 * `options` is `[{ value, label }]`; fully controlled via `value`/`onChange`.
 */
export const ChipTabs = ({ options, value, onChange }) => (
  <Stack direction="row" gap={0.875} flexWrap="wrap">
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <Box
          key={opt.value}
          component="button"
          type="button"
          onClick={() => onChange(opt.value)}
          sx={{
            font: 'inherit',
            fontSize: 12.5,
            fontWeight: active ? 700 : 600,
            px: 1.625,
            py: 0.875,
            border: 'none',
            borderRadius: 1,
            cursor: 'pointer',
            color: active ? 'common.white' : 'text.secondary',
            bgcolor: active ? 'error.main' : 'action.hover',
          }}
        >
          {opt.label}
        </Box>
      );
    })}
  </Stack>
);

/** Small pill badge for ReportHeader's top-right corner, e.g. "Day 137" or "Modeled". */
const HeaderBadge = ({ label, tone = 'default' }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      px: 1.25,
      py: 0.5,
      borderRadius: 1.5,
      fontSize: 12.5,
      fontWeight: 700,
      ...(tone === 'warning'
        ? { bgcolor: 'warning.lighter', color: 'warning.darker' }
        : { bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }),
    }}
  >
    {label}
  </Box>
);

/**
 * Per-report page header: title + subtitle on the left, optional pill
 * badges (e.g. "Day 137", "Modeled") and a short confidentiality/source
 * note on the right, bottom border - matching HD-10's `.head`. Every report
 * tab uses this so the experience is consistent regardless of which tab is
 * active.
 */
export const ReportHeader = ({ title, subtitle, badges, metaLines }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="flex-start"
    flexWrap="wrap"
    gap={1.25}
    sx={{ borderBottom: '2px solid', borderColor: 'text.primary', pb: 1.75 }}
  >
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
    <Stack alignItems="flex-end" gap={1}>
      {badges && (
        <Stack direction="row" gap={1}>
          {badges.filter(Boolean).map((b) => (
            <HeaderBadge key={b.label} label={b.label} tone={b.tone} />
          ))}
        </Stack>
      )}
      {metaLines && (
        <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'right' }}>
          {metaLines.map((line, i) => (
            <span key={line}>
              {i > 0 && <br />}
              {line}
            </span>
          ))}
        </Typography>
      )}
    </Stack>
  </Stack>
);

/** Color for a tracking-status pill (Awaiting / Below / On / Above target). */
export const getStatusPillColor = (theme, statusKey) =>
  ({
    awaiting: theme.palette.grey[500],
    behind: theme.palette.error.main,
    on: theme.palette.success.main,
    ahead: theme.palette.info.dark,
  }[statusKey] ?? theme.palette.grey[500]);

/** Small filled status pill, e.g. for vote-tracking precinct rows. */
export const StatusPill = ({ statusKey, label }) => {
  const theme = useTheme();
  const color = getStatusPillColor(theme, statusKey);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        minWidth: 74,
        textAlign: 'center',
        px: 1,
        py: 0.25,
        borderRadius: 0.75,
        fontWeight: 600,
        fontSize: 11.5,
        color,
        bgcolor: `${color}1F`,
      }}
    >
      {label}
    </Box>
  );
};

/**
 * Tan/warning callout box, matching HD-10's `.caveat`. `color` picks which
 * theme palette to source from (`warning` by default, matching every
 * existing caveat on these reports) - pass `"info"` for callouts that are
 * more "FYI, here's a default in use" than "something needs attention."
 */
export const Caveat = ({ children, color = 'warning', sx }) => (
  <Box
    sx={{
      bgcolor: `${color}.lighter`,
      border: '1px solid',
      borderColor: `${color}.light`,
      color: `${color}.darker`,
      borderRadius: 1,
      px: 2,
      py: 1.25,
      ...sx,
    }}
  >
    <Typography variant="caption" sx={{ fontSize: 12.5 }}>
      {children}
    </Typography>
  </Box>
);
